import React, { useEffect, useMemo } from "react";
import { Notification } from "@progress/kendo-react-notification";
import NotificationManager, { NotificationToast } from "./NotificationManager";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { mergeStyleSets } from "@fluentui/react";
import { getHTMLElementAsButtonProps } from "../accessibility/HTMLElementAsButtonARIA";
import LoadingManager from "../loading/LoadingManager";
import { ElaborationMessage } from "../../redux/feature/object/remote-elaborations-slice";

interface NotificationMessageProps {
  notification: NotificationToast;
  showModalDetails?: (details: ElaborationMessage[]) => void;
  onHide?: (notification: NotificationToast) => void;
}

function isElapsed(notif: NotificationToast) {
  if (!notif.isTemporary || notif.duration === undefined || notif.startShowing == null) return false;
  return new Date().getTime() >= notif.startShowing + notif.duration;
}

export const reloadPage = () => location.reload();

const classNames = mergeStyleSets({
  notificationDecoBorder: {
    height: "100%",
    width: "7px",
    minWidth: "7px",
    borderRadius: "3px",
  },
  notificationContainer: { display: "flex", flexDirection: "row", height: "100%", gap: "8px" },
  notificationIcon: { width: "16px", minWidth: "16px", height: "16px", marginTop: "2px" },
});
const NotificationItem: React.FunctionComponent<NotificationMessageProps> = ({
  notification,
  showModalDetails,
  onHide,
}) => {
  useEffect(() => {
    if (!notification.isTemporary) return;

    const interval = setInterval(() => {
      if (!notification.isTemporary) return;

      if (LoadingManager.hasActiveItems()) {
        NotificationManager.updateStartShowing(notification.key, new Date().getTime());
        return;
      }

      if (isElapsed(notification)) {
        onHide(notification);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [notification]);

  const { notificationColor, iconSrc } = useMemo(() => {
    if (notification.type === "success")
      return { iconSrc: "/assets/success.svg", notificationColor: "var(--color-green)" };
    if (notification.type === "error") return { iconSrc: "/assets/error.svg", notificationColor: "var(--color-red)" };
    if (notification.type === "warning")
      return { iconSrc: "/assets/warning.svg", notificationColor: "var(--color-yellow)" };
    if (notification.type === "info") return { iconSrc: "/assets/success.svg", notificationColor: "var(--color-blue)" };
    if (notification.type === "waiting") return { iconSrc: "/assets/info.png", notificationColor: "var(--color-blue)" };
  }, [notification.type]);

  return (
    <Notification
      type={{ style: "none", icon: false }}
      closable={notification.closable}
      onClose={() => onHide(notification)}
    >
      <div className={classNames.notificationContainer}>
        <div
          className={classNames.notificationDecoBorder}
          style={{
            backgroundColor: notificationColor,
          }}
        />
        <img src={iconSrc} alt={notification.type} className={classNames.notificationIcon} />
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {notification.title && <span className="notificationTitle">{notification.title?.toString()}</span>}
          <span className="bodyText">{notification.message?.toString()}</span>
          <div>
            {notification.enableReloadPanel && (
              <span
                {...getHTMLElementAsButtonProps({
                  onClick: reloadPage,
                })}
                style={{ textDecoration: "underline", float: "right" }}
              >
                {formatMessage("cof.base.refreshpanel")}
              </span>
            )}
            {Boolean(notification.details?.length) && (
              <span
                {...getHTMLElementAsButtonProps({
                  onClick: () => showModalDetails(notification.details),
                })}
                style={{ textDecoration: "underline", float: "left" }}
              >
                {formatMessage("cof.base.details")}
              </span>
            )}
          </div>
        </div>
      </div>
    </Notification>
  );
};
export default NotificationItem;
